import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { isNumber } from 'lodash'
import constants_work_experience from '../../constants/work-experience'
import { useLanguageContext } from '../../context'
import { mutation, query } from '../../graphql'
import Panel from '../tailwind/Panel'
import SpaceDetailItem from './space-detail-item'

const SpaceDetailsProfile = ({ space, career, refetchSpace }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const [files, setFiles] = useState([])

  const { firstname, lastname, email, partner_user_id, createdAt } = space

  const { title: career_title } = career || {}

  const additional_requested_data = JSON.parse(
    space.additional_requested_data || '{}'
  )

  const { work_experience } = additional_requested_data

  const handleDownloadFile = (fileKey) => {
    // Dateiname aus dem Pfad extrahieren
    const fileName = fileKey.split('/').pop()

    query({
      query: 'fileControl',
      variables: {
        space_id: space.id,
        action: 'downloadFile',
        file_name: fileName
      }
    })
      .then((response) => {
        try {
          const responseData = JSON.parse(response)
          if (responseData && responseData.body) {
            const bodyData = JSON.parse(responseData.body)
            if (bodyData && bodyData.fileContent && bodyData.contentType) {
              // Base64-String in Blob umwandeln
              const byteCharacters = atob(bodyData.fileContent)
              const byteArrays = []

              for (
                let offset = 0;
                offset < byteCharacters.length;
                offset += 512
              ) {
                const slice = byteCharacters.slice(offset, offset + 512)
                const byteNumbers = new Array(slice.length)

                for (let i = 0; i < slice.length; i++) {
                  byteNumbers[i] = slice.charCodeAt(i)
                }

                const byteArray = new Uint8Array(byteNumbers)
                byteArrays.push(byteArray)
              }

              const blob = new Blob(byteArrays, { type: bodyData.contentType })
              const url = URL.createObjectURL(blob)

              // Datei herunterladen
              const a = document.createElement('a')
              a.href = url
              a.download = fileName
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              URL.revokeObjectURL(url)
            }
          }
        } catch (parseError) {
          console.error(
            'Fehler beim Parsen der Dateiinformationen:',
            parseError
          )
        }
      })
      .catch((error) => {
        alert(error)
        console.error('Fehler beim Abrufen der Datei:', error)
      })
  }

  useEffect(() => {
    // Prüfen, ob der Space Dokumente hat
    query({
      query: 'fileControl',
      variables: {
        space_id: space.id,
        action: 'listFilesForSpace'
      }
    })
      .then((response) => {
        // Die gesamte Antwort ist ein String, der geparst werden muss
        try {
          const responseData = JSON.parse(response)
          if (responseData && responseData.body) {
            const bodyData = JSON.parse(responseData.body)
            if (bodyData && bodyData.files && Array.isArray(bodyData.files)) {
              setFiles(bodyData.files)
            }
          }
        } catch (parseError) {
          console.error(
            'Fehler beim Parsen der Dateiinformationen:',
            parseError
          )
        }
      })
      .catch((error) => {
        alert(error)
        console.error('Fehler beim Abrufen der Dateien:', error)
      })
  }, [space.id])

  const formik = useFormik({
    initialStatus: {},
    enableReinitialize: true,
    initialValues: {
      firstname:
        space.person?.firstname || space.person?.invitation_firstname || '',
      lastname:
        space.person?.lastname || space.person?.invitation_lastname || ''
    },
    validationSchema: Yup.object({
      firstname: Yup.string(),
      lastname: Yup.string()
    }),
    onSubmit: ({ firstname, lastname }) => {
      Promise.all([
        mutation({
          mutation: 'updatePerson',
          input: { id: space.person.id, firstname, lastname }
        }),
        // update space.updatedAt
        mutation({ mutation: 'updateSpace', input: { id: space.id } })
      ]).then(refetchSpace)
    }
  })

  const profile = {
    basics: [
      {
        available: true,
        term: t('talent.state_invited'),
        details: format(new Date(createdAt), 'd. MMMM yyyy', {
          locale: { de, en }[language]
        })
      },
      {
        available: !!partner_user_id,
        term: t('pages.talent.space_details_partner_user_id_label'),
        details: partner_user_id
      },
      {
        available: true,
        term: 'Assessment-ID',
        details: space.id
      },
      {
        available: !!space.owner,
        term: 'Aivy Talent-ID (Intern)',
        details: space.owner
      },
      {
        available: !!email,
        term: t('pages.talent.space_details_email_label'),
        details: (
          <a href={`mailto:${email}`} className='hover:text-blue-600'>
            {email}
          </a>
        )
      },
      {
        available: !!space.person,
        term: t('pages.talent.space_details_firstname_label'),
        details: firstname,
        input: { id: 'firstname', formik }
      },
      {
        available: !!space.person,
        term: t('pages.talent.space_details_lastname_label'),
        details: lastname,
        input: { id: 'lastname', formik }
      },
      {
        available: isNumber(work_experience),
        term: t(
          `pages.talent.space_details_${
            career_title ? 'career' : 'overall'
          }_work_experience_label`,
          { career_title }
        ),
        details: isNumber(work_experience)
          ? constants_work_experience[work_experience].label[language]
          : ''
      },
      {
        available: files.length > 0,
        term: t('pages.talent.space_details_documents_label', {
          defaultValue: 'Dokumente'
        }),
        details: (
          <div className='space-y-2'>
            {files.map((file, index) => (
              <div key={index} className='flex items-center justify-between'>
                <span className='max-w-xs truncate'>
                  {file.key.split('/').pop()}
                </span>
                <button
                  onClick={() => handleDownloadFile(file.key)}
                  className='ml-2 rounded bg-blue-500 px-2 py-1 text-xs text-white hover:bg-blue-600'
                >
                  {t('pages.talent.space_details_download', {
                    defaultValue: 'Herunterladen'
                  })}
                </button>
              </div>
            ))}
          </div>
        )
      },

      ...Object.keys(additional_requested_data)
        .filter((key) => key !== 'work_experience') // work_experience wird separat behandelt
        .map((key) => {
          const data = additional_requested_data[key]

          // Robuste Verarbeitung der Eingabewerte
          let displayValue = ''
          let fieldLabel = key

          try {
            if (data && typeof data === 'object') {
              // Das Label ist direkt in den Daten enthalten
              fieldLabel =
                data.label ||
                key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

              // Verarbeite die Eingabewerte basierend auf dem Typ
              if (data.multiple && Array.isArray(data.input)) {
                // Für Mehrfachauswahl: Zeige alle ausgewählten Werte an
                displayValue = data.input
                  .filter(
                    (item) => item && typeof item === 'object' && item.value
                  ) // Filter leere Objekte
                  .map((item) => item.value)
                  .join(', ')
              } else if (Array.isArray(data.input) && data.input.length > 0) {
                // Für Einzelauswahl: Zeige den ersten Wert an
                const firstItem = data.input[0]
                if (
                  firstItem &&
                  typeof firstItem === 'object' &&
                  firstItem.value
                ) {
                  displayValue = firstItem.value
                } else if (firstItem) {
                  displayValue = String(firstItem)
                }
              } else if (
                data.input &&
                typeof data.input === 'object' &&
                data.input.value
              ) {
                // Für einzelne Dropdown-Werte
                displayValue = data.input.value
              } else if (data.input) {
                // Fallback für andere Formate (z.B. Text-Input)
                displayValue = String(data.input)
              } else if (data.value) {
                // Alternative Struktur mit value-Property
                displayValue = String(data.value)
              }

              // Prüfe ob es ein Datei-Feld ist
              if (data.type === 'file') {
                return { available: false }
              }
            } else if (data) {
              // Fallback wenn data direkt der Wert ist
              displayValue = String(data)
              fieldLabel = key
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())
            }
          } catch (error) {
            console.warn(
              `Error processing additional data for key ${key}:`,
              error
            )
            displayValue = String(data || '')
            fieldLabel = key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (l) => l.toUpperCase())
          }

          // Nur anzeigen wenn es einen sinnvollen Wert gibt
          if (
            !displayValue ||
            displayValue === '' ||
            displayValue === 'undefined' ||
            displayValue === 'null'
          ) {
            return { available: false }
          }

          // Formatiere das Label (ersetze Unterstriche durch Leerzeichen)
          const formattedLabel = fieldLabel.replace(/_/g, ' ')

          return {
            available: true,
            term: formattedLabel,
            details: displayValue
          }
        })
    ].filter(({ available }) => available)
  }

  return (
    <Panel>
      <section
        className='md:h-full'
        aria-labelledby='applicant-information-title'
      >
        <div className='md:h-full'>
          <div className='pb-5 sm:flex'>
            <div>
              <h2
                id='applicant-information-title'
                className='text-lg font-medium leading-6 text-gray-900'
              >
                {t('pages.talent.space_details_profile_header')}
              </h2>
              <p className='mt-1 max-w-2xl text-sm text-gray-500'>
                {t('pages.talent.space_details_profile_description')}
              </p>
            </div>
          </div>
          <div className='border-t border-gray-300 pt-5'>
            <dl className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              {profile.basics.map(
                ({ term, details, onClick, input }, index) => (
                  <SpaceDetailItem
                    key={index}
                    {...{ term, details, onClick, input }}
                  />
                )
              )}
            </dl>
          </div>
        </div>
      </section>
    </Panel>
  )
}

SpaceDetailsProfile.propTypes = {
  space: PropTypes.object.isRequired,
  career: PropTypes.object,
  refetchSpace: PropTypes.func.isRequired
}

SpaceDetailsProfile.defaultProps = {
  career: null
}

export default SpaceDetailsProfile
